<template>
  <div class="d-flex justify-content-between card-message pt-4 pb-4">
    <div class="d-flex flex-row">
      <div class="container__profile">
        <base-avatar-user-group
          v-if="channel.isLearner(user.id)"
          class="mr-2"
          :users="channelTeachers"
          :limit="2"
          :overlap="0.5"
        >
        </base-avatar-user-group>

        <div v-else class="d-flex align-items-center card-message__profile mr-2">
          <div class="text-center card-message__profile-text" style="width: 100%">
            {{ shortName }}
          </div>
        </div>
      </div>

      <div class="d-flex flex-column card-message__content text-left">
        <div
          class="font-weight-bold text-primary text-capitalize text__chat-lu-learner-name text__chat-learner-name"
        >
          {{ userNameOrLearnningUnitName }}
        </div>

        <div v-if="showLearningUnit" class="text-primary">
          {{ learningUnitNameOrSection }}
        </div>

        <div v-if="showSection && !showLastMessage" class="text-black-50 text__chat-section">
          {{ sectionStartToEndOrLastMessages }}
        </div>

        <div v-if="showLastMessage" class="text-black-50 mt-2">
          <div v-if="lastMessage" class="card-message__truncate-overflow text__chat-latest-message">
            <font-awesome-icon
              v-if="isLastMessageFileType"
              :icon="{ prefix: 'far', iconName: 'file' }"
              size="sm"
              class="text-nuetal-primary"
            />

            {{ lastMessage }}
          </div>

          <small v-else>
            <div>
              {{ `You did not have any messages with ${userNameOrLearnningUnitName}.` }}
            </div>
            <div>{{ `Click to start the conversation now.` }}</div>
          </small>
        </div>
      </div>
    </div>
    <div class="d-flex flex-column justify-content-between text-right">
      <BadgeMessageCount :count="messageCount" class="text__unread-number" />

      <MessageDate :date="latestMessageDate" class="text-nowrap text__latest-message-date" />
    </div>
  </div>
</template>

<script>
import moment from 'moment';
import BaseAvatarUserGroup from '@/components/base-components/BaseAvatarUserGroup';
import BadgeMessageCount from './BadgeMessageCount.vue';
import MessageDate from './MessageDate.vue';
import LearningUnit from '@/models/LearningUnit';
import Learner from '@/models/Learner';
import Section from '@/models/Section';

export default {
  name: 'MessageCard',

  components: {
    BaseAvatarUserGroup,
    BadgeMessageCount,
    MessageDate
  },

  props: {
    channel: {
      type: Object,
      default: null
    },
    user: {
      type: Object,
      default: null
    },

    showLearningUnit: {
      type: Boolean,
      default: true
    },

    showSection: {
      type: Boolean,
      default: true
    },

    showLastMessage: {
      type: Boolean,
      default: true
    }
  },

  data() {
    return {
      shortName: '',
      userNameOrLearnningUnitName: '',
      learningUnitNameOrSection: '',
      sectionStartToEndOrLastMessages: '',
      latestMessageDate: null,
      isLastMessageFileType: false,
      lastMessage: null,
      messageCount: null
    };
  },

  computed: {
    isLearnerInChannel() {
      return this.channel && this.user && this.channel.isLearner(this.user.id);
    },

    channelTeachers() {
      return this.channel ? this.channel.teachers() : [];
    }
  },

  watch: {
    channel: {
      immediate: true,
      deep: true,
      handler: 'onChangeChannel'
    }
  },

  methods: {
    userProfileText(name) {
      if (!name) return '';

      const words = name.split(' ');
      if (words.length === 1) return words[0][0].toUpperCase();
      const shortName = words[0][0] + words[words.length - 1][0];

      return shortName.toUpperCase();
    },

    sectionStartToEndString(sectionStart, sectionEnd) {
      return (
        moment(sectionStart).format('MMM D, YYYY') +
        ' - ' +
        moment(sectionEnd).format('MMM D, YYYY')
      );
    },

    async onChangeChannel(channel) {
      if (!channel || !this.user) return;

      try {
        const { metadata, lastMessage, unreadMessageCount = null } = channel;
        const { learner, learning_unit, section } = metadata;
        const learningUnit = LearningUnit.find(learning_unit);

        if (lastMessage) {
          const { createdAt, message, name, messageType } = lastMessage;
          this.latestMessageDate = new Date(createdAt);

          this.isLastMessageFileType = messageType === 'file';

          this.lastMessage = messageType === 'file' ? name : message;
        }

        if (unreadMessageCount) {
          this.messageCount = unreadMessageCount;
        }

        const sectionModel = Section.find(section);
        const learnerModel = Learner.find(learner);

        if (this.user.id == parseInt(learner)) {
          if (learningUnit) {
            const { name } = learningUnit;
            this.userNameOrLearnningUnitName = name;
            this.shortName = this.userProfileText(name);
          }

          if (sectionModel) {
            const { start_at, end_at } = sectionModel;

            this.learningUnitNameOrSection = `(${this.sectionStartToEndString(start_at, end_at)})`;
          }

          this.sectionStartToEndOrLastMessages = this.lastMessage;
        } else {
          if (learnerModel) {
            const { name } = learnerModel;
            this.shortName = this.userProfileText(name);
            this.userNameOrLearnningUnitName = name;
          }

          if (learningUnit) {
            const { name } = learningUnit;
            this.learningUnitNameOrSection = `(${name})`;
          }

          if (sectionModel) {
            const { start_at, end_at } = sectionModel;

            this.sectionStartToEndOrLastMessages = this.sectionStartToEndString(start_at, end_at);
          }
        }
      } catch (error) {
        console.error(error);
      }
    }
  }
};
</script>

<style lang="scss">
.card-message {
  border-bottom: 1px solid rgba(7, 9, 25, 0.125);

  .card-message__content {
    max-width: 75%;
  }

  .container__profile {
    width: 96px;
    display: flex;
    justify-content: center;

    .b-avatar-group .b-avatar-group-inner {
      justify-content: center;
    }
  }

  .card-message__profile {
    min-width: 44px;
    max-width: 44px;
    height: 44px;
    background-color: rgba(122, 123, 151, 1);
    border-radius: 50%;
  }

  .card-message__profile-text {
    font-size: 19.2px;
    color: rgba(255, 255, 255, 1);
  }

  .card-message__truncate-overflow {
    --lh: 1.4rem;
    line-height: var(--lh);
    --max-lines: 1;
    position: relative;
    max-height: calc(var(--lh) * var(--max-lines));
    overflow: hidden;
    padding-right: 1rem; /* space for ellipsis */
  }

  // .limit-text {
  //   white-space: nowrap;
  //   overflow: hidden;
  //   text-overflow: ellipsis;
  //   // max-width: 50ch;

  //   // @media only screen and (max-width: 600px) {
  //   //   max-width: 25ch;
  //   // }
  // }
}
</style>
